import type {
  IntakeDryRunResult,
  IntakeMappingSuggestionResult,
  IntakeSource,
  IntakeSourceKeyMinted,
  IntakeSourceList,
  IntakeSourcePatchInput,
  IntakeSourceStatusReport,
  IntakeSourceWriteInput,
  IntakeValidationResult,
} from '@ofmos/shared/intake';
import type {
  IntakeApplyResult,
  IntakeClient,
  IntakePullNow,
  IntakeValidateInput,
} from '../client';
import type { Transport } from './transport';

/**
 * `IntakeClient` over `/api/intake-sources` (`docs/decisions/inbound-intake.md` D15).
 *
 * As thin as `HttpReportClient`, and for the same reason: what a legal source IS gets
 * decided once, on the server, by `intakeSourceWriteSchema` plus `validateSource`'s checks
 * against live metadata. Re-checking any of it here would be a second validator that says
 * yes to everything the first one already accepts and drifts the day a field type is added.
 *
 * THE SAVE AND THE VALIDATE ROUTE ANSWER THE SAME LIST IN TWO SHAPES, which is the one
 * thing a caller of this module has to know (`DEVIATIONS-INBOUND.md` entry 5): a refused
 * save is an `invalid_state` `ApiError` whose `details.issues` are the issues, and
 * `validate` is a 200 carrying them under `issues`. `app/intake/issues.ts` reads the first
 * shape back out, so no screen unpacks an error body by hand.
 */
export class HttpIntakeClient implements IntakeClient {
  constructor(private readonly transport: Transport) {}

  async list(): Promise<IntakeSourceList> {
    return this.transport.request<IntakeSourceList>({
      method: 'GET',
      path: '/api/intake-sources',
    });
  }

  async get(sourceId: string): Promise<IntakeSource> {
    const response = await this.transport.request<{ source: IntakeSource }>({
      method: 'GET',
      path: `/api/intake-sources/${sourceId}`,
    });
    return response.source;
  }

  async create(input: IntakeSourceWriteInput): Promise<IntakeSource> {
    const response = await this.transport.request<{ source: IntakeSource }>({
      method: 'POST',
      path: '/api/intake-sources',
      body: input,
    });
    return response.source;
  }

  async patch(sourceId: string, patch: IntakeSourcePatchInput): Promise<IntakeSource> {
    const response = await this.transport.request<{ source: IntakeSource }>({
      method: 'PATCH',
      path: `/api/intake-sources/${sourceId}`,
      body: patch,
    });
    return response.source;
  }

  async remove(sourceId: string): Promise<void> {
    await this.transport.request<void>({
      method: 'DELETE',
      path: `/api/intake-sources/${sourceId}`,
    });
  }

  async setCredential(sourceId: string, secret: string): Promise<string> {
    const response = await this.transport.request<{ credentialSetAt: string }>({
      method: 'POST',
      path: `/api/intake-sources/${sourceId}/credential`,
      body: { secret },
    });
    return response.credentialSetAt;
  }

  async validate(
    sourceId: string,
    candidate: IntakeValidateInput = {},
  ): Promise<IntakeValidationResult> {
    return this.transport.request<IntakeValidationResult>({
      method: 'POST',
      path: `/api/intake-sources/${sourceId}/validate`,
      body: candidate,
    });
  }

  async mintKey(sourceId: string): Promise<IntakeSourceKeyMinted> {
    const response = await this.transport.request<{ apiKey: IntakeSourceKeyMinted }>({
      method: 'POST',
      path: `/api/intake-sources/${sourceId}/key`,
      body: {},
    });
    return response.apiKey;
  }

  async dryRun(sourceId: string, payload: unknown): Promise<IntakeDryRunResult> {
    return this.transport.request<IntakeDryRunResult>({
      method: 'POST',
      path: `/api/intake-sources/${sourceId}/dry-run`,
      body: { payload },
    });
  }

  async suggestMapping(sourceId: string, sample: unknown): Promise<IntakeMappingSuggestionResult> {
    return this.transport.request<IntakeMappingSuggestionResult>({
      method: 'POST',
      path: `/api/intake-sources/${sourceId}/mapping/suggest`,
      body: { sample },
    });
  }

  async pullNow(sourceId: string): Promise<IntakePullNow> {
    const response = await this.transport.request<{ queued?: boolean; pull?: IntakePullNow }>({
      method: 'POST',
      path: `/api/intake-sources/${sourceId}/pull`,
      body: {},
    });
    // Two answers from one route (202 queued, 200 with the counts). The 200 nests them.
    return response.pull ?? { queued: true };
  }

  async status(sourceId: string): Promise<IntakeSourceStatusReport> {
    return this.transport.request<IntakeSourceStatusReport>({
      method: 'GET',
      path: `/api/intake-sources/${sourceId}/status`,
    });
  }

  async applySubmission(recordId: string): Promise<IntakeApplyResult> {
    return this.transport.request<IntakeApplyResult>({
      method: 'POST',
      path: `/api/intake/submissions/${recordId}/apply`,
      body: {},
    });
  }
}
